import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const OfflineContext = createContext();

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineData, setOfflineData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load cached offline data on mount
  useEffect(() => {
    loadOfflineData();
  }, []);

  // Load offline data from IndexedDB
  const loadOfflineData = async () => {
    try {
      const stored = localStorage.getItem('egns_offline_data');
      if (stored) {
        const data = JSON.parse(stored);
        setOfflineData(data);
        setLastSyncTime(data.generated_at);
      }
    } catch (error) {
      console.warn('Failed to load offline data from localStorage:', error);
    }
  };

  // Download content for offline use
  const downloadOfflineContent = useCallback(async (level = null) => {
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const url = level 
        ? `${API_URL}/api/offline/package?level=${level}`
        : `${API_URL}/api/offline/package`;
      
      setDownloadProgress(20);
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download offline content');
      }
      
      setDownloadProgress(60);
      
      const data = await response.json();
      
      // Store in localStorage (with size limit check)
      const dataStr = JSON.stringify(data);
      if (dataStr.length < 5 * 1024 * 1024) { // 5MB limit for localStorage
        localStorage.setItem('egns_offline_data', dataStr);
      } else {
        // For larger data, use IndexedDB
        await storeInIndexedDB(data);
      }
      
      setDownloadProgress(80);
      
      // Cache in service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_OFFLINE_DATA',
          data: data
        });
      }
      
      setDownloadProgress(100);
      setOfflineData(data);
      setLastSyncTime(data.generated_at);
      
      return { success: true, stats: data.stats };
    } catch (error) {
      // error handled silently
      return { success: false, error: error.message };
    } finally {
      setIsDownloading(false);
    }
  }, []);

  // Store large data in IndexedDB
  const storeInIndexedDB = (data) => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('egns_offline', 1);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('content')) {
          db.createObjectStore('content', { keyPath: 'id' });
        }
      };
      
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['content'], 'readwrite');
        const store = transaction.objectStore('content');
        
        store.put({ id: 'offline_package', ...data });
        
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };
      
      request.onerror = () => reject(request.error);
    });
  };

  // Add pending change for later sync
  const addPendingChange = useCallback((type, data) => {
    const change = {
      id: Date.now(),
      type,
      data,
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => {
      const updated = [...prev, change];
      localStorage.setItem('egns_pending_changes', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Sync pending changes when online
  const syncPendingChanges = useCallback(async () => {
    if (!isOnline || pendingChanges.length === 0) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${API_URL}/api/offline/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lesson_completions: pendingChanges.filter(c => c.type === 'lesson_completion').map(c => c.data),
          flashcard_reviews: pendingChanges.filter(c => c.type === 'flashcard_review').map(c => c.data),
          exercise_answers: pendingChanges.filter(c => c.type === 'exercise_answer').map(c => c.data)
        })
      });
      
      if (response.ok) {
        setPendingChanges([]);
        localStorage.removeItem('egns_pending_changes');
        return { success: true };
      }
    } catch (error) {
      console.warn('Failed to sync pending offline changes:', error);
    }
    
    return { success: false };
  }, [isOnline, pendingChanges]);

  // Get offline flashcards
  const getOfflineFlashcards = useCallback((level = null, category = null) => {
    if (!offlineData?.content?.flashcards) return [];
    
    let cards = offlineData.content.flashcards;
    
    if (level) {
      cards = cards.filter(c => c.lesson_id?.toLowerCase().startsWith(level.toLowerCase()));
    }
    
    if (category) {
      cards = cards.filter(c => c.category === category);
    }
    
    return cards;
  }, [offlineData]);

  // Get offline stories
  const getOfflineStories = useCallback((level = null) => {
    if (!offlineData?.content?.stories) return [];
    
    let stories = offlineData.content.stories;
    
    if (level) {
      stories = stories.filter(s => s.level === level);
    }
    
    return stories;
  }, [offlineData]);

  // Get offline lessons
  const getOfflineLessons = useCallback((level = null) => {
    if (!offlineData?.content?.lessons) return [];
    
    let lessons = offlineData.content.lessons;
    
    if (level) {
      lessons = lessons.filter(l => l.level === level);
    }
    
    return lessons;
  }, [offlineData]);

  // Clear offline data
  const clearOfflineData = useCallback(() => {
    localStorage.removeItem('egns_offline_data');
    setOfflineData(null);
    setLastSyncTime(null);
    
    // Clear service worker cache
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
    }
  }, []);

  const value = {
    isOnline,
    offlineData,
    isDownloading,
    downloadProgress,
    lastSyncTime,
    pendingChanges,
    downloadOfflineContent,
    addPendingChange,
    syncPendingChanges,
    getOfflineFlashcards,
    getOfflineStories,
    getOfflineLessons,
    clearOfflineData,
    hasOfflineData: !!offlineData
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
